import { Link } from "react-router-dom";
import { Sparkles, Heart, Users, Handshake } from "lucide-react";
import PageHero from "../components/PageHero";
import Breadcrumbs from "../components/Breadcrumbs";
import ReviewsStrip from "../components/ReviewsStrip";
import BookCallCTA from "../components/BookCallCTA";

const PILLARS = [
  {
    icon: Sparkles,
    title: "Quality you can inspect",
    body: "Renovated rooms with Euro-top mattresses, 50-inch LED TVs, LED-lit granite baths, and daily-cleaning standards we're proud to have you check. Nothing on the amenity list is aspirational — if we say it's here, it's here.",
  },
  {
    icon: Handshake,
    title: "Service from real people",
    body: "The front desk is staffed 24 hours. The phone rings straight through — no automated tree, no offshore call center. If we say we'll do something, we do it. If we get it wrong, we own it.",
  },
  {
    icon: Users,
    title: "Built around the customer",
    body: "The reason you're in Clinton is what your stay should be built around — fishing, cycling, hunting, work, a wedding, a family reunion, or just a stop on a long drive. We keep the amenities that serve those reasons and skip the ones that don't.",
  },
  {
    icon: Heart,
    title: "Gratitude",
    body: "Thank you for having us be part of your journey and your life. We know staying in Clinton is a choice, and we know Westbridge is a choice. We'd like to earn it every time.",
  },
];

export default function AboutPage() {
  return (
    <>
      <PageHero
        eyebrow="Our story"
        title="A bridge between the road and a good night's rest."
        intro="Westbridge Inn & Suites is a 32-room independent inn in Clinton, Missouri — a hospitality brand that keeps the promise simple: great service, clean rooms."
        image="/images/exterior-6.jpg"
        metaTitle="About Westbridge Inn & Suites — Clinton, MO"
        metaDesc="Meet Westbridge Inn & Suites in Clinton, Missouri. 32 renovated rooms, 24-hour front desk, real hospitality — and the story behind the bridge."
      />
      <Breadcrumbs items={[{ label: "About" }]} />

      {/* Brand meaning */}
      <section className="bg-[#FDFBF7]">
        <div className="wb-container py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <img src="/images/lobby-1.jpg" alt="Front desk at Westbridge Inn & Suites, Clinton MO" className="w-full aspect-[4/5] object-cover" />
          </div>
          <div className="lg:col-span-7">
            <div className="wb-eyebrow">Brand meaning</div>
            <div className="wb-divider mt-4" />
            <h2 className="wb-h2 mt-6">A bridge carries you across the long middle of a journey.</h2>
            <p className="wb-body mt-6">
              That's where the name comes from. A journey is rarely just the destination — it's the long middle, the drive between where you were and where you're going. A bridge is what carries you over the parts you can't just walk through: rivers, valleys, tiredness, road weariness.
            </p>
            <p className="wb-body mt-4">
              Westbridge is that bridge. Between the road and a good night's rest. Between an early tournament blast-off and the coffee you needed to survive it. Between a long day on the Katy Trail and the shower that made it worth it. Between a family gathering and a real bed to send the kids to.
            </p>
            <p className="wb-body mt-4">
              <strong className="text-[#1A1A1A]">Great service. Clean rooms.</strong> That's the whole slogan. And also the whole job.
            </p>
          </div>
        </div>
      </section>

      {/* Four Pillars */}
      <section className="bg-[#F5F0E6] border-y border-[#E5DFD3]">
        <div className="wb-container py-16 md:py-24">
          <div className="max-w-2xl mb-12">
            <div className="wb-eyebrow">Four pillars</div>
            <h2 className="wb-h2 mt-4">The way we run the inn.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {PILLARS.map(({ icon: Icon, title, body }) => (
              <article key={title} className="wb-card p-8 bg-white" data-testid={`pillar-${title.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="w-11 h-11 rounded-sm bg-[#FCEEEA] flex items-center justify-center mb-4">
                  <Icon className="h-5 w-5 text-[#B94A15]" />
                </div>
                <h3 className="wb-h3">{title}</h3>
                <p className="wb-body mt-3">{body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Locally rooted */}
      <section className="bg-[#FDFBF7]">
        <div className="wb-container py-16 md:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="wb-eyebrow">Locally rooted</div>
            <h2 className="wb-h2 mt-4">A Clinton hotel, run by people who live here.</h2>
            <p className="wb-body mt-5">
              Clinton is the seat of Henry County, home to about 9,000 people, and the western terminus of the 240-mile Katy Trail State Park. It's the kind of town where the courthouse still sits in the middle of everything and locals still say hello. We're part of that fabric — a local business, staffed by locals, answering the phone in a way you can only do when the people on the other end of the phone know the streets you're asking about.
            </p>
            <p className="wb-body mt-4">
              When you call the front desk and ask where to eat, we don't consult a tablet — we actually tell you where <em>we'd</em> eat. When you ask which Truman Lake ramp to launch from, we tell you the one we'd pick for the wind that day. That's what a local hotel is <em>for</em>.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img src="/images/exterior-1.jpg" alt="Westbridge Inn & Suites exterior in Clinton, Missouri" className="w-full aspect-square object-cover" />
            <img src="/images/exterior-4.jpg" alt="Westbridge Inn & Suites property view" className="w-full aspect-square object-cover mt-8" />
            <img src="/images/exterior-6.jpg" alt="Westbridge Inn & Suites building" className="w-full aspect-square object-cover" />
            <img src="/images/pool-1.jpg" alt="Seasonal outdoor pool at Westbridge Inn & Suites" className="w-full aspect-square object-cover mt-8" />
          </div>
        </div>
      </section>

      {/* Hospitality family note */}
      <section className="bg-[#1A1A1A] text-white">
        <div className="wb-container py-16 md:py-20">
          <div className="max-w-3xl">
            <div className="wb-eyebrow text-[#FF9666]">Part of something bigger</div>
            <h2 className="wb-h2 text-white mt-4">A hospitality brand. Independently operated.</h2>
            <p className="wb-body text-white/85 mt-5">
              Westbridge is a hospitality brand — a family of independently operated Midwest hotels sharing the same promise: quality you can inspect, service from real people, and stays built around the customer. Each property is run by the people who live in that town.
            </p>
            <p className="wb-body text-white/85 mt-4">
              What that means for you: local ownership decisions, local service standards, and a name that stands for one thing across every property.
            </p>
          </div>
        </div>
      </section>

      <ReviewsStrip />

      {/* Thank you closing */}
      <section className="bg-[#FDFBF7]">
        <div className="wb-container py-16 md:py-24 text-center">
          <div className="wb-eyebrow inline-block">Gratitude</div>
          <h2 className="wb-h2 mt-4 max-w-3xl mx-auto">
            Thank you for having us be part of your journey.
          </h2>
          <p className="wb-body mt-6 max-w-2xl mx-auto">
            Every guest that walks through our door is choosing to trust us with something — a family celebration, a work run, a fishing weekend, a night that had to go right. We don't take that lightly. We'd like to earn it again.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/rooms" className="wb-btn-outline" data-testid="about-see-rooms">See the rooms</Link>
            <Link to="/local-guide" className="wb-btn-ghost" data-testid="about-local-guide">Explore the neighborhood</Link>
          </div>
        </div>
      </section>

      <BookCallCTA />
    </>
  );
}
